/**
 * Analysis Mode Hook
 * 
 * Manages the Analysis Mode state and logic for debugging/configuring
 * avatar behavior through voice conversation.
 * 
 * App-agnostic: uses SessionServices for API calls and auth.
 */

import { useState, useCallback, useRef } from 'react';
import { ToolDefinition } from '@selna/session';
import {
  formatSchemaForPrompt,
  formatCurrentConfig,
  getInferenceHeuristics,
} from '@wfvr/scenario-schema';
import {
  AUDIO_HONESTY_INSTRUCTION,
  getLanguageInstruction,
  type Language,
} from '@wfvr/prompt-instructions';
import { useSessionServices } from '../context/SessionServicesContext';
import { getLocalizedCharacterName } from '../utils/characterName';
import { logger } from '../utils/logger';

export interface AnalysisModeState {
  isActive: boolean;
  isProcessing: boolean;
  lastError: string | null;
  changesApplied: boolean;
}

export interface ScenarioData {
  id: string;
  behavioralProfile?: Record<string, any>;
  avatarRules?: string[];
  aiHiddenContext?: string;
  avatarKnowledge?: string;
  establishedFacts?: string;
  userReferenceInfo?: string;  // User-only field - does NOT require prompt regeneration
  description?: string;
  aiRole?: string;
  userRole?: string;
  characterName?: string;
}

export interface TranscriptMessage {
  text: string;
  speaker: 'user' | 'ai';
  timestamp: number;
}

export interface UseAnalysisModeParams {
  scenarioId: string;
  scenarioData: ScenarioData | null;
  /** Called to get the current scenario prompt (to restore on exit without changes) */
  getCurrentPrompt: () => string | null;
  /** Called when entering Analysis Mode - should return Promise that resolves when session is updated */
  onPromptReady: (prompt: string) => Promise<void> | void;
  /** Called when exiting Analysis Mode - should return Promise that resolves when session is updated */
  onNewPromptAvailable: (prompt: string) => Promise<void> | void;
  sendFunctionResult: (callId: string, result: string | object) => void;
}

export interface UseAnalysisModeReturn {
  state: AnalysisModeState;
  /** Enter Analysis Mode - returns Promise that resolves when session is fully switched */
  enter: (recentMessages: TranscriptMessage[]) => Promise<void>;
  /** Exit Analysis Mode - returns Promise that resolves when session is fully restored */
  exit: (newPrompt?: string) => Promise<void>;
  handleFunctionCall: (callId: string, name: string, args: Record<string, any>) => Promise<void>;
  getTools: () => ToolDefinition[];
  buildAnalysisModePrompt: (recentMessages: TranscriptMessage[]) => string;
}

/**
 * Hook for managing Analysis Mode
 */
export function useAnalysisMode({
  scenarioId,
  scenarioData,
  getCurrentPrompt,
  onPromptReady,
  onNewPromptAvailable,
  sendFunctionResult,
}: UseAnalysisModeParams): UseAnalysisModeReturn {
  const services = useSessionServices();
  
  const [state, setState] = useState<AnalysisModeState>({
    isActive: false,
    isProcessing: false,
    lastError: null,
    changesApplied: false,
  });
  
  // Ref to track changesApplied without causing callback recreation
  const changesAppliedRef = useRef(false);

  // Store the original prompt to restore on exit without changes
  const originalPromptRef = useRef<string | null>(null);
  // Store the latest prompt after changes
  const latestPromptRef = useRef<string | null>(null);

  /**
   * Build the Analysis Mode prompt
   * 
   * IMPORTANT: This prompt includes critical runtime instructions (language, audio honesty)
   * to ensure consistent behavior with normal scenario prompts.
   */
  const buildAnalysisModePrompt = useCallback((recentMessages: TranscriptMessage[]): string => {
    if (!scenarioData) {
      return 'Analysis mode error: No scenario data available.';
    }

    // characterName may be a multilingual object ({ en, pt, ... }) at runtime.
    // Localize it to a plain string here, otherwise template-literal interpolation
    // below stringifies the object to "[object Object]".
    const localizedCharacterName = scenarioData.characterName
      ? getLocalizedCharacterName(scenarioData.characterName, services.getCurrentLanguage())
      : undefined;

    const currentConfig = formatCurrentConfig({
      behavioralProfile: scenarioData.behavioralProfile,
      avatarRules: scenarioData.avatarRules,
      aiHiddenContext: scenarioData.aiHiddenContext,
      avatarKnowledge: scenarioData.avatarKnowledge,
      establishedFacts: scenarioData.establishedFacts,
      userReferenceInfo: scenarioData.userReferenceInfo,
      description: scenarioData.description,
      aiRole: scenarioData.aiRole,
      userRole: scenarioData.userRole,
      characterName: localizedCharacterName,
    });

    const schema = formatSchemaForPrompt();
    const heuristics = getInferenceHeuristics();

    // Format recent messages for context
    let conversationContext = '';
    if (recentMessages && recentMessages.length > 0) {
      const last10 = recentMessages.slice(-10);
      conversationContext = `
RECENT CONVERSATION (before analysis mode):
${last10.map(m => `${m.speaker === 'user' ? 'User' : 'Avatar'}: ${m.text}`).join('\n')}

Use this context to understand what behavior the technician might want to change.
`;
    }

    const characterName = localizedCharacterName || 'the avatar';

    // Get language instruction for the current user language
    // This ensures the AI responds in the correct language
    const currentLanguage = services.getCurrentLanguage() as Language;
    const languageInstruction = getLanguageInstruction(currentLanguage);

    // Build the complete Analysis Mode prompt with all sections
    // CRITICAL: Language instruction goes FIRST (highest priority) to ensure correct language
    // Note: We include AUDIO_HONESTY_INSTRUCTION and Language instruction
    // but skip INITIAL_GREETING, ROLE_MAINTENANCE, CONVERSATION_COMPLETENESS
    // as they don't apply to Analysis Mode
    return `${languageInstruction}

---

${AUDIO_HONESTY_INSTRUCTION}

---

ANALYSIS MODE ACTIVE

You are now in Analysis Mode. You were playing the character "${characterName}" but a technician has activated diagnostic mode to analyze and configure your behavior.

SESSION DURATION:
- You are in a technical configuration session. 
- Normal session duration limits do NOT apply in Analysis Mode. 
- You should remain active as long as the technician needs to configure your behavior.
- Do NOT call end_conversation while in Analysis Mode. Only use exit_analysis_mode when requested.

CRITICAL - FUNCTION CALL VALUES MUST BE IN ENGLISH:
The system architecture uses English as the base language. All translations to other languages are generated from the English content. Therefore:
- ALL values you pass in function calls MUST be in English, regardless of the conversation language
- This applies to: description, aiHiddenContext, avatarKnowledge, establishedFacts, avatarRules, userReferenceInfo, aiRole, userRole, characterName
- Even if the technician gives you instructions in another language, you MUST write the actual field values in English
- You may confirm and discuss changes in the technician's language, but the values you submit in function calls must always be English text

CRITICAL - CHARACTER NAME PLACEHOLDER:
The system supports multilingual character names. Each character may have different names in different languages (e.g., "Charles" in English, "Carlos" in Portuguese).
- When writing content that references the character by name, use the placeholder {CHARACTER_NAME} instead of the actual name
- The frontend will automatically substitute the correct localized name based on the user's language
- Example: Instead of "Charles is a middle-aged man who manages the IT department", write "{CHARACTER_NAME} is a middle-aged man who manages the IT department"
- This ensures descriptions work correctly across all supported languages

CRITICAL - HOW YOU SHOULD SPEAK IN ANALYSIS MODE:
- Be CONCISE. Say what needs to be said in as few words as possible.
- DO NOT repeat yourself. If you explained something once, don't explain it again.
- DO NOT be verbose or expand unnecessarily. Get to the point.
- Speak naturally as if to a colleague, but keep it SHORT.
- No lists, bullet points, or formatted text. Just brief, clear sentences.

YOUR ROLE IN ANALYSIS MODE:
You are being debugged and configured by a technician. Your job is to:
1. Explain why you behaved a certain way when asked
2. Diagnose which configuration fields caused specific behaviors
3. Suggest specific changes to fix issues
4. Apply changes when the technician approves

${currentConfig}

${schema}

${heuristics}
${conversationContext}
TOOL USAGE - CRITICAL:
When the technician approves a change, you MUST:
1. FIRST say something like "Okay, applying that change now. This may take a moment." or "Sure, updating that now, please wait."
2. THEN call the appropriate tool
This is essential because tool calls can take several seconds and silence feels broken.

Available tools:
- update_scenario_fields: To update one or more configuration fields
- add_avatar_rule: To add a new behavioral rule
- remove_avatar_rule: To remove an existing rule by index
- exit_analysis_mode: When the technician wants to return to normal conversation
- lock_analysis_mode: When the technician wants to permanently lock analysis mode for this scenario (no one will be able to enter via voice after this)

CONVERSATION GUIDELINES:
- Be BRIEF. Diagnose the issue and suggest a fix in 2-3 sentences max.
- Do NOT over-explain. The technician understands the system.
- When asked why you did something, identify the relevant config and explain briefly.
- Suggest specific value changes, not vague adjustments.
- Always confirm with the technician before applying changes.
- ALWAYS announce before calling a tool - never leave silence while processing.
- After applying changes, briefly confirm what was done (one sentence).`;
  }, [scenarioData, services]);

  /**
   * Enter Analysis Mode
   * Returns a Promise that resolves when the session has been updated with the Analysis Mode prompt
   */
  const enter = useCallback(async (recentMessages: TranscriptMessage[]): Promise<void> => {
    logger.info('[AnalysisMode] Entering analysis mode');
    
    // Store the current prompt so we can restore it on exit without changes
    const currentPrompt = getCurrentPrompt();
    if (currentPrompt) {
      originalPromptRef.current = currentPrompt;
      logger.debug('[AnalysisMode] Saved original prompt for restoration');
    } else {
      console.warn('[AnalysisMode] No current prompt available to save');
    }
    
    // Reset latest prompt ref (will be updated if changes are made)
    latestPromptRef.current = null;
    
    setState(prev => ({
      ...prev,
      isActive: true,
      isProcessing: false,
      lastError: null,
      changesApplied: false,
    }));

    const prompt = buildAnalysisModePrompt(recentMessages);
    
    // Wait for session to be updated before returning
    // This ensures the new prompt is applied before any response is generated
    await onPromptReady(prompt);
    
    logger.debug('[AnalysisMode] Session updated with Analysis Mode prompt');
  }, [getCurrentPrompt, buildAnalysisModePrompt, onPromptReady]);

  /**
   * Exit Analysis Mode
   * Returns a Promise that resolves when the session has been restored
   */
  const exit = useCallback(async (newPrompt?: string): Promise<void> => {
    logger.info('[AnalysisMode] Exiting analysis mode, changesApplied:', changesAppliedRef.current);
    
    // Reset ref
    changesAppliedRef.current = false;
    
    setState({
      isActive: false,
      isProcessing: false,
      lastError: null,
      changesApplied: false,
    });

    // Use the new prompt if provided (after changes), otherwise use latest or original
    // Await to ensure session is fully restored before returning
    const promptToUse = newPrompt || latestPromptRef.current || originalPromptRef.current;
    if (promptToUse) {
      await onNewPromptAvailable(promptToUse);
    }
  }, [onNewPromptAvailable]);

  /**
   * Handle function calls from the AI
   */
  const handleFunctionCall = useCallback(async (
    callId: string,
    name: string,
    args: Record<string, any>
  ): Promise<void> => {
    logger.debug('[AnalysisMode] Handling function call:', { name, hasArgs: Boolean(args) });

    setState(prev => ({ ...prev, isProcessing: true }));

    try {
      switch (name) {
        case 'update_scenario_fields': {
          const { updates, reasoning, regenerateUserReferenceInfo } = args;
          
          // Validate updates object exists and is not empty
          if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
            logger.warn('[AnalysisMode] Invalid updates object received:', { updates, args });
            sendFunctionResult(callId, {
              success: false,
              message: 'Error: The updates object is missing or empty. Please specify which fields to update using valid field paths from the Analysis Mode schema.',
            });
            break;
          }

          const result = await services.analysisUpdateScenario(
            scenarioId,
            updates,
            reasoning,
            regenerateUserReferenceInfo
          );
          
          // Store the new prompt
          latestPromptRef.current = result.prompt;
          
          // Update both state and ref
          changesAppliedRef.current = true;
          setState(prev => ({ ...prev, changesApplied: true }));
          
          sendFunctionResult(callId, {
            success: true,
            message: `Updated fields: ${result.updatedFields.join(', ')}. The changes have been applied and the prompt has been regenerated.`,
          });
          break;
        }

        case 'add_avatar_rule': {
          const { rule } = args;
          
          // Get current rules and add new one
          const currentRules = scenarioData?.avatarRules || [];
          const updates = {
            avatarRules: [...currentRules, rule],
          };
          
          const result = await services.analysisUpdateScenario(
            scenarioId,
            updates,
            `Added rule: ${rule}`
          );

          latestPromptRef.current = result.prompt;
          
          // Update both state and ref
          changesAppliedRef.current = true;
          setState(prev => ({ ...prev, changesApplied: true }));
          
          sendFunctionResult(callId, {
            success: true,
            message: `Added new rule: "${rule}". The prompt has been regenerated.`,
          });
          break;
        }

        case 'remove_avatar_rule': {
          const { ruleIndex } = args;
          
          const currentRules = scenarioData?.avatarRules || [];
          if (ruleIndex < 0 || ruleIndex >= currentRules.length) {
            sendFunctionResult(callId, {
              success: false,
              message: `Invalid rule index: ${ruleIndex}. Valid indices are 0 to ${currentRules.length - 1}.`,
            });
            break;
          }
          
          const removedRule = currentRules[ruleIndex];
          const newRules = currentRules.filter((_: string, i: number) => i !== ruleIndex);
          
          const result = await services.analysisUpdateScenario(
            scenarioId,
            { avatarRules: newRules },
            `Removed rule at index ${ruleIndex}: ${removedRule}`
          );

          latestPromptRef.current = result.prompt;
          
          // Update both state and ref
          changesAppliedRef.current = true;
          setState(prev => ({ ...prev, changesApplied: true }));
          
          sendFunctionResult(callId, {
            success: true,
            message: `Removed rule: "${removedRule}". The prompt has been regenerated.`,
          });
          break;
        }

        case 'exit_analysis_mode': {
          // restartConversation is available in args but currently not used
          // const { restartConversation } = args;
          
          // Use ref to get the latest value (avoids stale closure)
          sendFunctionResult(callId, {
            success: true,
            message: changesAppliedRef.current 
              ? 'Exiting analysis mode. Changes have been applied and the conversation will restart with the updated configuration.'
              : 'Exiting analysis mode. Returning to normal conversation.',
          });
          
          // Small delay to let the AI respond before switching
          setTimeout(async () => {
            try {
              await exit(latestPromptRef.current || undefined);
            } catch (error) {
              console.error('[AnalysisMode] Error exiting analysis mode:', error);
            }
          }, 500);
          break;
        }

        case 'lock_analysis_mode': {
          // Lock analysis mode for this scenario - prevents future access via voice
          await services.analysisUpdateScenario(
            scenarioId,
            { analysisModeEnabled: false },
            'Technician locked analysis mode via voice command'
          );

          sendFunctionResult(callId, {
            success: true,
            message: 'Analysis mode has been locked for this scenario. No one will be able to enter analysis mode via voice command. To unlock, an administrator must edit the scenario in the backoffice.',
          });
          
          // Exit analysis mode after locking
          setTimeout(async () => {
            try {
              await exit(latestPromptRef.current || undefined);
            } catch (error) {
              console.error('[AnalysisMode] Error exiting analysis mode after lock:', error);
            }
          }, 500);
          break;
        }

        default:
          sendFunctionResult(callId, {
            success: false,
            message: `Unknown function: ${name}`,
          });
      }
    } catch (error) {
      console.error('[AnalysisMode] Function call error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, lastError: errorMessage }));
      
      sendFunctionResult(callId, {
        success: false,
        message: `Error: ${errorMessage}`,
      });
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  }, [scenarioId, scenarioData, sendFunctionResult, exit, services]);

  /**
   * Get the tools for Analysis Mode
   */
  const getTools = useCallback((): ToolDefinition[] => {
    return services.config.analysisModeEditTools || services.config.analysisModeTools;
  }, [services]);

  return {
    state,
    enter,
    exit,
    handleFunctionCall,
    getTools,
    buildAnalysisModePrompt,
  };
}
